import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMasters, nameOf } from "@/lib/masters";
import { PageHeader, Card, Input, Select, Field, Btn, Chip } from "@/components/kit";
import { exportXlsx } from "@/lib/export";
import { toast, Toaster } from "sonner";
import { UploadSimple, FileText, DownloadSimple, Warning } from "@phosphor-icons/react";
import * as XLSX from "xlsx";
import { istDateString } from "@/lib/date";

const TARGETS = [
  { key: "plate_size", label: "Plate Size" },
  { key: "location", label: "Workshop / Location" },
  { key: "quantity", label: "Quantity" },
  { key: "opening_date", label: "Opening Date" },
  { key: "remarks", label: "Remarks" },
  { key: "_ignore", label: "— ignore —" },
];

function autoMap(header) {
  const h = (header || "").toString().trim().toLowerCase();
  if (!h) return "_ignore";
  if (/(plate.*size|size$)/.test(h)) return "plate_size";
  if (/(workshop|location|godown|unit|machine)/.test(h)) return "location";
  if (/(qty|quantity|count|stock|opening)$/.test(h) && !/date/.test(h)) return "quantity";
  if (/date/.test(h)) return "opening_date";
  if (/remark|note/.test(h)) return "remarks";
  return "_ignore";
}

const STATUS_TONE = { ok: "good", ambiguous: "warn", invalid: "bad", not_found: "bad", missing: "neutral" };

export default function OpeningStock() {
  const m = useMasters();
  const { user } = useAuth();
  const isAdmin = user && (user.role === "super_admin" || user.role === "admin");
  const [showReset, setShowReset] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetReason, setResetReason] = useState("Fresh CRON opening stock");
  const [resetting, setResetting] = useState(false);
  const today = istDateString();
  const [resetDate, setResetDate] = useState(today);

  // --- Single-entry form (workshop = machine, auto-derived) ---
  const [form, setForm] = useState({
    plate_size_id: "", location_id: "",
    quantity: 0, opening_date: today, remarks: "",
  });

  // Auto-derive machine from workshop (location name matches machine name)
  const deriveMachineId = (location_id) => {
    const loc = m.locations.find(l => l.id === location_id);
    if (!loc) return null;
    const mc = m.machines.find(x => x.name.toLowerCase() === loc.name.toLowerCase());
    return mc?.id || null;
  };

  const submitSingle = async () => {
    if (!form.location_id) return toast.error("Select a Workshop.");
    if (!form.plate_size_id) return toast.error("Select a Plate Size.");
    const machine_id = deriveMachineId(form.location_id);
    if (!machine_id) {
      return toast.error("Workshop name must match a stock location (AMSKY/KODAK/CRON/PARIBESHAK). Rename the workshop in Master Data → Workshops / Locations.");
    }
    try {
      await api.post("/inventory/opening_stock", { ...form, machine_id, quantity: parseInt(form.quantity) });
      toast.success("Opening stock added");
      setForm({ ...form, quantity: 0, remarks: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Bulk upload state ---
  const [sheet, setSheet] = useState(null);
  const [batchId, setBatchId] = useState("");
  const [mapping, setMapping] = useState({});
  const [rows, setRows] = useState([]);
  const [defaultDate, setDefaultDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const commitInFlight = useRef(false);

  const downloadTemplate = () => {
    // Workshop = machine, so we only need Plate Size × Location combinations.
    const template = [];
    const activePlates = (m.plate_sizes || []).filter(x => x.active !== false);
    const activeLocs = (m.locations || []).filter(x => x.active !== false);
    if (activePlates.length === 0 || activeLocs.length === 0) {
      template.push({ "Plate Size": "550X700", Workshop: "AMSKY", Quantity: 0, "Opening Date": today, Remarks: "" });
    } else {
      for (const ps of activePlates) {
        for (const lc of activeLocs) {
          template.push({
            "Plate Size": ps.canonical,
            Workshop: lc.name,
            Quantity: 0,
            "Opening Date": today,
            Remarks: "",
          });
        }
      }
    }
    exportXlsx("opening_stock_template.xlsx", template, "Opening Stock");
    toast.success(`Template with ${template.length} rows downloaded. Fill in Quantity and re-upload.`);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setResults(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const arr = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
      if (arr.length < 2) return toast.error("File has no data rows.");
      const headers = arr[0].map(h => String(h).trim());
      const data = arr.slice(1).filter(r => r.some(c => String(c).trim() !== ""));
      setSheet({ headers, rows: data });
      setRows([]);
      setBatchId(globalThis.crypto?.randomUUID?.() || `batch-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const map = {}; headers.forEach(h => { map[h] = autoMap(h); }); setMapping(map);
      toast.success(`Loaded ${data.length} rows, ${headers.length} columns.`);
    } catch (err) { toast.error("Could not read file: " + err.message); }
    finally { setBusy(false); }
  };

  const analyse = async () => {
    if (!sheet) return toast.error("Upload a file first.");
    const targets = {};
    Object.entries(mapping).forEach(([h, t]) => { if (t !== "_ignore") targets[t] = h; });
    if (!targets.plate_size || !targets.location || !targets.quantity) {
      return toast.error("Please map Plate Size, Workshop/Location and Quantity columns.");
    }
    setBusy(true);
    try {
      const parseRows = sheet.rows.map(r => {
        const obj = {};
        Object.entries(targets).forEach(([target, header]) => {
          const idx = sheet.headers.indexOf(header);
          if (idx >= 0) obj[target] = String(r[idx] ?? "").trim();
        });
        return obj;
      });
      const { data } = await api.post("/inventory/opening_stock/parse_rows", { rows: parseRows, default_date: defaultDate });
      const built = data.results.map((r, i) => ({
        idx: i,
        parse: r,
        machine_id: r.fields.machine?.id || "",
        plate_size_id: r.fields.plate_size?.id || "",
        location_id: r.fields.location?.id || "",
        quantity: r.fields.quantity?.value ?? "",
        opening_date: r.fields.opening_date?.value || defaultDate,
        remarks: r.fields.remarks?.value || "",
      }));
      setRows(built);
      toast.success(`Parsed ${built.length} rows.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const isReady = (r) => r.machine_id && r.plate_size_id && r.location_id && r.quantity !== "" && parseInt(r.quantity) >= 0 && r.opening_date;
  const readyCount = rows.filter(isReady).length;
  const skipZeroCount = rows.filter(r => isReady(r) && parseInt(r.quantity) === 0).length;

  const updateRow = (idx, patch) => setRows(rs => rs.map(r => r.idx === idx ? { ...r, ...patch } : r));

  const commit = async () => {
    if (commitInFlight.current) return toast.error("This opening-stock upload is already being committed.");
    const ready = rows.filter(isReady);
    if (ready.length === 0) return toast.error("No rows are ready.");
    const nonZero = ready.filter(r => parseInt(r.quantity) > 0).length;
    if (!window.confirm(`Commit opening stock? ${nonZero} rows will create stock entries. ${ready.length - nonZero} rows have quantity=0 and will be skipped.`)) return;
    commitInFlight.current = true;
    setBusy(true);
    try {
      const payload = ready.map(r => ({
        machine_id: r.machine_id,
        plate_size_id: r.plate_size_id,
        location_id: r.location_id,
        quantity: parseInt(r.quantity),
        opening_date: r.opening_date,
        remarks: r.remarks || null,
      }));
      const { data } = await api.post("/inventory/opening_stock/bulk", { rows: payload, batch_id: batchId });
      setResults(data);
      setRows([]);
      setSheet(null);
      toast.success(`Created ${data.created}. ${data.skipped>0?data.skipped+' skipped (qty=0).':''} ${data.failed>0?data.failed+' failed.':''}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { commitInFlight.current = false; setBusy(false); }
  };

  const runReset = async () => {
    if (resetConfirm.trim() !== "RESET CRON STOCK") {
      return toast.error('Type exactly "RESET CRON STOCK" to confirm.');
    }
    setResetting(true);
    try {
      const { data } = await api.post("/inventory/reset_cron_stock", {
        reason: resetReason || "Fresh CRON opening stock",
        reset_date: resetDate,
        confirm_text: resetConfirm.trim(),
      });
      toast.success(`CRON stock is now zero. ${data.groups_reset} balance(s) adjusted; upload the fresh opening-stock sheet next.`);
      setShowReset(false); setResetConfirm("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResetting(false); }
  };

  return (
    <div data-testid="opening-stock-page">
      <Toaster position="bottom-right" richColors />
      <PageHeader
        title="Opening Stock"
        subtitle="Opening stock adds to the ledger. Reset CRON first when replacing its balances."
        actions={isAdmin ? (
          <Btn variant="danger" onClick={() => setShowReset(true)} data-testid="opening-reset-btn">
            <Warning size={14} /> Reset CRON Stock
          </Btn>
        ) : null}
      />

      <div className="p-6 space-y-6">
        <Card>
          <div className="p-4 border-l-4 border-cyan-500 bg-cyan-50/60">
            <div className="font-heading font-black uppercase tracking-wider text-sm">Safe CRON stock replacement</div>
            <p className="text-sm text-zinc-700 mt-1">
              Use <span className="font-semibold">Reset CRON Stock</span> once, confirm CRON shows zero, then upload the fresh CRON sheet once.
              The reset uses audited adjustments and does not affect AMSKY, KODAK or PARIBESHAK.
            </p>
          </div>
        </Card>
        {/* Single entry */}
        <Card>
          <div className="px-4 py-3 border-b border-zinc-200 font-heading font-bold text-sm uppercase tracking-wider">Single entry</div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Opening Date*"><Input type="date" value={form.opening_date} onChange={e=>setForm({...form,opening_date:e.target.value})} /></Field>
            <Field label="Workshop*">
              <Select value={form.location_id} onChange={e=>setForm({...form,location_id:e.target.value})} data-testid="opening-location">
                <option value="">—</option>{m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
              </Select>
            </Field>
            <Field label="Plate Size*">
              <Select value={form.plate_size_id} onChange={e=>setForm({...form,plate_size_id:e.target.value})} data-testid="opening-size">
                <option value="">—</option>{m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
              </Select>
            </Field>
            <Field label="Quantity*"><Input type="number" min="0" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})} data-testid="opening-qty" /></Field>
            <Field label="Remarks"><Input value={form.remarks} onChange={e=>setForm({...form,remarks:e.target.value})} /></Field>
          </div>
          <div className="flex justify-end px-5 py-3 border-t border-zinc-200">
            <Btn onClick={submitSingle} data-testid="opening-submit-btn">Add Opening Stock</Btn>
          </div>
        </Card>

        {/* Bulk upload */}
        <Card>
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="font-heading font-bold text-sm uppercase tracking-wider">Bulk upload from Excel</div>
            <Btn variant="outline" onClick={downloadTemplate} data-testid="opening-download-template">
              <DownloadSimple size={14} /> Download Template
            </Btn>
          </div>
          <div className="p-5 space-y-4">
            <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-sm p-3 leading-relaxed">
              <div className="font-semibold text-zinc-900 mb-1">How to use:</div>
              1. Click <span className="font-semibold">Download Template</span> — a pre-filled spreadsheet with every Machine × Plate Size × Location combination (Quantity=0).<br/>
              2. Open in Excel/Google Sheets and enter the actual counted quantity for each row (leave 0 for combinations with no stock).<br/>
              3. Save and re-upload here.<br/>
              4. Verify the preview and click <span className="font-semibold">Commit</span>.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-zinc-300 rounded-sm px-4 py-6 cursor-pointer hover:border-zinc-500 bg-zinc-50/50" data-testid="opening-file-drop">
                <UploadSimple size={28} className="text-zinc-400" />
                <div className="mt-2 text-sm font-semibold">{sheet ? `Loaded: ${sheet.rows.length} rows` : "Click to select .xlsx / .csv"}</div>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile} className="hidden" data-testid="opening-file-input" />
              </label>
              <div>
                <Field label="Default Opening Date">
                  <Input type="date" value={defaultDate} onChange={e=>setDefaultDate(e.target.value)} data-testid="opening-bulk-date" />
                </Field>
                <div className="text-[11px] text-zinc-500 mt-1 leading-snug">Used for rows where the "Opening Date" column is blank.</div>
              </div>
            </div>

            {sheet && (
              <div className="border border-zinc-200 rounded-sm p-3 bg-white">
                <div className="text-xs font-semibold text-zinc-700 mb-2 flex items-center gap-1">
                  <FileText size={12} /> Column mapping ({sheet.headers.length} columns detected)
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sheet.headers.map(h => (
                    <div key={h} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-zinc-600 truncate flex-1" title={h}>{h}</span>
                      <span className="text-zinc-400">→</span>
                      <Select value={mapping[h] || "_ignore"} onChange={e=>setMapping({...mapping, [h]: e.target.value})} className="text-xs min-w-0">
                        {TARGETS.map(t=><option key={t.key} value={t.key}>{t.label}</option>)}
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3">
                  <Btn onClick={analyse} disabled={busy} data-testid="opening-analyse-btn">Analyse Rows</Btn>
                </div>
              </div>
            )}

            {rows.length > 0 && (
              <>
                <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
                  <div className="font-heading font-bold text-sm uppercase tracking-wider">
                    Preview — {readyCount}/{rows.length} ready
                    {skipZeroCount > 0 && <span className="ml-2 text-xs font-normal text-zinc-600">({skipZeroCount} with qty=0 will be skipped)</span>}
                  </div>
                  <Btn variant="primary" onClick={commit} disabled={busy || readyCount === 0} data-testid="opening-commit-btn">
                    Commit {readyCount - skipZeroCount} entries
                  </Btn>
                </div>
                <div className="overflow-x-auto border border-zinc-200 rounded-sm">
                  <table className="w-full data-table" data-testid="opening-preview-table">
                    <thead><tr>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Status</th>
                      <th className="px-2 py-2 text-left">Workshop</th>
                      <th className="px-2 py-2 text-left">Plate Size</th>
                      <th className="px-2 py-2 text-right">Quantity</th>
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Problems</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(r => {
                        const ready = isReady(r);
                        const zero = parseInt(r.quantity) === 0;
                        const problems = r.parse?.overall?.errors || [];
                        return (
                          <tr key={r.idx} className={!ready ? "bg-red-50/40" : zero ? "opacity-50" : ""}>
                            <td className="px-2 py-2 font-mono text-[11px]">{r.idx+1}</td>
                            <td className="px-2 py-2">
                              {!ready ? <Chip tone="bad">Blocked</Chip> : zero ? <Chip tone="neutral">Skip (0)</Chip> : <Chip tone="good">Ready</Chip>}
                            </td>
                            <td className="px-2 py-2">
                              <Select value={r.location_id} onChange={e=>{
                                const loc_id = e.target.value;
                                const loc = m.locations.find(l => l.id === loc_id);
                                const mc = loc ? m.machines.find(x => x.name.toLowerCase() === loc.name.toLowerCase()) : null;
                                updateRow(r.idx, { location_id: loc_id, machine_id: mc?.id || "" });
                              }} className="text-xs">
                                <option value="">—</option>{m.locations.map(x=><option key={x.id} value={x.id}>{x.name}</option>)}
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              <Select value={r.plate_size_id} onChange={e=>updateRow(r.idx,{plate_size_id:e.target.value})} className="text-xs">
                                <option value="">—</option>{m.plate_sizes.map(x=><option key={x.id} value={x.id}>{x.canonical}</option>)}
                              </Select>
                            </td>
                            <td className="px-2 py-2 text-right">
                              <input type="number" min="0" value={r.quantity} onChange={e=>updateRow(r.idx,{quantity:e.target.value})} className="border border-zinc-300 rounded-sm px-1 py-0.5 text-xs w-20 text-right" />
                            </td>
                            <td className="px-2 py-2">
                              <input type="date" value={r.opening_date} onChange={e=>updateRow(r.idx,{opening_date:e.target.value})} className="border border-zinc-300 rounded-sm px-1 py-0.5 text-xs" />
                            </td>
                            <td className="px-2 py-2 text-[11px] text-red-700 max-w-[220px]">
                              {problems.length === 0 ? <span className="text-emerald-700">—</span> : problems.slice(0,2).join(" · ")}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {results && (
              <div className="border border-zinc-200 rounded-sm p-4 bg-white">
                <div className="font-heading font-bold text-sm uppercase tracking-wider mb-2">Results</div>
                <div className="flex gap-3 flex-wrap">
                  <Chip tone="good">Created: {results.created}</Chip>
                  <Chip tone="neutral">Skipped (qty=0): {results.skipped}</Chip>
                  <Chip tone={results.failed>0?"bad":"neutral"}>Failed: {results.failed}</Chip>
                </div>
                {results.results.filter(r=>!r.ok).length > 0 && (
                  <ul className="mt-3 text-[12px] space-y-1">
                    {results.results.filter(r=>!r.ok).map(r => (
                      <li key={r.index} className="text-red-700">
                        Row {r.index+1}: {typeof r.error === "object" ? `${r.error.problem||''} ${r.error.resolution||''}` : String(r.error)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {showReset && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => !resetting && setShowReset(false)}>
          <div className="bg-white rounded-sm border-2 border-red-500 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="reset-stock-dialog">
            <div className="px-5 py-3 border-b-2 border-red-500 bg-red-50 flex items-center gap-2">
              <Warning size={20} className="text-red-700" weight="fill" />
              <div className="font-heading font-black uppercase tracking-wider text-red-900">Reset CRON Stock Only</div>
            </div>
            <div className="p-5 space-y-4 text-sm">
              <p className="leading-relaxed">
                This brings every current <span className="font-mono font-bold">CRON</span> plate balance to zero through audited adjustments.
              </p>
              <p className="text-zinc-700">
                Historical jobs and movements remain available. AMSKY, KODAK and PARIBESHAK are not changed.
                After this succeeds, upload the CRON opening-stock sheet once.
              </p>
              <Field label="Reset Date">
                <Input type="date" value={resetDate} onChange={e => setResetDate(e.target.value)} data-testid="reset-date" />
              </Field>
              <Field label="Reason (audit log)">
                <Input value={resetReason} onChange={e => setResetReason(e.target.value)} data-testid="reset-reason" />
              </Field>
              <Field label={<span>Type <span className="font-mono font-black">RESET CRON STOCK</span> to confirm</span>}>
                <Input
                  value={resetConfirm}
                  onChange={e => setResetConfirm(e.target.value)}
                  placeholder="RESET CRON STOCK"
                  data-testid="reset-confirm-input"
                  className="font-mono"
                />
              </Field>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-200 bg-zinc-50">
              <Btn variant="outline" onClick={() => setShowReset(false)} disabled={resetting} data-testid="reset-cancel-btn">Cancel</Btn>
              <Btn variant="danger" onClick={runReset} disabled={resetting || resetConfirm.trim() !== "RESET CRON STOCK"} data-testid="reset-confirm-btn">
                {resetting ? "Resetting..." : "Reset CRON Stock"}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
